// src/pages/PrivacyPolicy.js
import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  return (
    <div>
      <Helmet>
        <title>Privacy Policy - YouthXConnect</title>
        <meta
          name="description"
          content="Read YouthXConnect's Privacy Policy to understand how we collect, use, and protect your personal information."
        />
        <meta
          name="keywords"
          content="Privacy Policy, YouthXConnect, Data Protection, Personal Information, Privacy"
        />
        <meta property="og:title" content="Privacy Policy - YouthXConnect" />
        <meta
          property="og:description"
          content="Read YouthXConnect's Privacy Policy to understand how we collect, use, and protect your personal information."
        />
        <meta property="og:image" content="%PUBLIC_URL%/privacy-policy-og.png" />
        <meta property="og:url" content="https://www.youthxconnect.com/privacy-policy" />
        <meta name="twitter:title" content="Privacy Policy - YouthXConnect" />
        <meta
          name="twitter:description"
          content="Read YouthXConnect's Privacy Policy to understand how we collect, use, and protect your personal information."
        />
        <meta name="twitter:image" content="%PUBLIC_URL%/privacy-policy-twitter.png" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      <motion.div
        className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-20 max-w-4xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.h1
          className="text-3xl sm:text-4xl font-bold mb-6 text-center text-gray-900"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          Privacy Policy
        </motion.h1>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          This Privacy Policy ("Policy") constitutes a legally binding agreement between you ("User", "you", "your") and YouthXConnect ("Company", "we", "us", "our"). By accessing, browsing, or using our services, you acknowledge that you have read, understood, and agree to be bound by the terms of this Policy and consent to all actions we take with respect to your information consistent with this Policy.
        </motion.p>
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          Information Collection and Usage Disclaimer
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
          While we implement reasonable security measures, you explicitly acknowledge and accept that no method of transmission over the Internet or method of electronic storage is 100% secure or error-free. You expressly agree that your use of our services and any information transmitted to or by us is at your sole risk. Under no circumstances shall we be liable for any direct, indirect, incidental, special, consequential, or exemplary damages arising from any data breach, information misuse, or unauthorized access.
        </motion.p>
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
        >
          Limitation of Liability
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
          To the maximum extent permitted by applicable law, in no event shall YouthXConnect, its affiliates, agents, directors, employees, suppliers, or licensors be liable for any indirect, punitive, incidental, special, consequential, or exemplary damages, including without limitation damages for loss of profits, goodwill, use, data, or other intangible losses, arising out of or relating to your use of our services. This includes, but is not limited to:
          <ul className="list-disc pl-6 mt-2">
            <li>Any unauthorized access to or use of our servers and/or any personal information stored therein</li>
            <li>Any interruption or cessation of transmission to or from our services</li>
            <li>Any bugs, viruses, trojan horses, or the like that may be transmitted to or through our services</li>
            <li>Any errors or omissions in any content or data</li>
            <li>Any data breaches or information leaks, regardless of cause</li>
          </ul>
        </motion.p>
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
        >
          Third-Party Services and Data Processing
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
          We may employ third-party companies and individuals to facilitate our services ("Service Providers"). These third parties have access to your information only to perform specific tasks on our behalf and are contractually obligated to not disclose or use it for any other purpose. However, we explicitly disclaim any responsibility or liability for the actions, omissions, or policies of any third-party service providers. Your interactions with such third parties are governed by their respective privacy policies.
        </motion.p>
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.0 }}
        >
          Force Majeure
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}>
          We shall not be liable or responsible for any failure to perform, or delay in performance of, any of our obligations under this Policy that is caused by events outside our reasonable control, including but not limited to acts of God, war, pandemic, epidemic, civil commotion, malicious damage, compliance with any law or governmental order, rule, regulation or direction, accident, breakdown of systems or network infrastructure, fire, flood, storm or default of suppliers or sub-contractors.
        </motion.p>
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.2 }}
        >
          Changes to This Policy
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}>
          We may update our Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date.
        </motion.p>
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.4 }}
        >
          Contact Us
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}>
          If you have any questions about this Privacy Policy, please contact us at <a href="mailto:connect@youthxconnect.com" className="text-blue-500 underline">connect@youthxconnect.com</a>.
        </motion.p>
      </motion.div>
      
    </div>
  );
};

export default PrivacyPolicy;
